/*
 * StageCascadeRenderer — Kaskaden-Anzeige für Mehrkesselanlagen.
 *
 * One row per stage. Left → stage label. Middle → status pill driven by
 * `statePoint` (off = grey, released = amber, running = green). Right →
 * modulation bar from optional `loadPoint`. Optional per-stage runtime
 * footer from `runtimePoint`.
 *
 * Staleness rule: whole row goes 55% alpha when the state point quality is
 * bad or stale (Concept §1 — quality/staleness belong in the widget).
 *
 * Legend at the bottom explaining the three state colours plus the
 * modulation bar and runtime badge, so the operator does not need to guess.
 */

import { useEffect, useMemo } from 'preact/hooks';
import type { StageCascadeWidget, StageBinding } from '../widgets';
import type { IPointBindingSource, PointValue } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { formatNumber } from '../../format/leafValue';
import './StageCascadeRenderer.css';

interface Props {
  widget: StageCascadeWidget;
  source: IPointBindingSource;
}

type StageStatus = 'off' | 'released' | 'running' | 'unknown';

export function StageCascadeRenderer({ widget, source }: Props) {
  const loadUnit = widget.loadUnit ?? '%';

  return (
    <div class="hv-cascade">
      {widget.label && <div class="hv-cascade__label">{widget.label}</div>}

      <div class="hv-cascade__rows" role="list">
        {widget.stages.map((stage, idx) => (
          <StageRow
            key={`${stage.label}-${idx}`}
            stage={stage}
            source={source}
            loadUnit={loadUnit}
          />
        ))}
      </div>

      <footer class="hv-cascade__legend">
        <span class="hv-cascade__legend-item">
          <span class="hv-cascade__pill hv-cascade__pill--off">Aus</span>
          <span class="hv-cascade__legend-hint">Stufe abgeschaltet</span>
        </span>
        <span class="hv-cascade__legend-item">
          <span class="hv-cascade__pill hv-cascade__pill--released">Freigabe</span>
          <span class="hv-cascade__legend-hint">Bereit, wartet auf Anforderung</span>
        </span>
        <span class="hv-cascade__legend-item">
          <span class="hv-cascade__pill hv-cascade__pill--running">Läuft</span>
          <span class="hv-cascade__legend-hint">Kessel moduliert aktiv</span>
        </span>
      </footer>
    </div>
  );
}

// ---------- One stage row -------------------------------------------------

function StageRow({ stage, source, loadUnit }: {
  stage: StageBinding;
  source: IPointBindingSource;
  loadUnit: string;
}) {
  const stateSig = useMemo(
    () => pointSignal(source, stage.statePoint),
    [source, stage.statePoint],
  );
  useEffect(() => stateSig.release, [stateSig]);

  const loadSig = useMemo(
    () => (stage.loadPoint ? pointSignal(source, stage.loadPoint) : null),
    [source, stage.loadPoint],
  );
  useEffect(() => loadSig?.release ?? (() => {}), [loadSig]);

  const runtimeSig = useMemo(
    () => (stage.runtimePoint ? pointSignal(source, stage.runtimePoint) : null),
    [source, stage.runtimePoint],
  );
  useEffect(() => runtimeSig?.release ?? (() => {}), [runtimeSig]);

  const statePv = stateSig.value.value;
  const loadPv = loadSig?.value.value ?? null;
  const runtimePv = runtimeSig?.value.value ?? null;

  const status = classifyState(statePv);
  const stale = isDegraded(statePv);

  const load = coerceNumber(loadPv?.value);
  const runtimeHours = coerceNumber(runtimePv?.value);

  const statusLabel: Record<StageStatus, string> = {
    off: 'Aus',
    released: 'Freigabe',
    running: 'Läuft',
    unknown: '—',
  };

  return (
    <div class={`hv-cascade__row${stale ? ' hv-cascade__row--stale' : ''}`} role="listitem">
      <div class="hv-cascade__label-col">{stage.label}</div>

      <div class="hv-cascade__status-col">
        <span class={`hv-cascade__pill hv-cascade__pill--${status}`}>
          {statusLabel[status]}
        </span>
      </div>

      <div class="hv-cascade__load-col">
        {load != null ? (
          <div class="hv-cascade__load-bar" title={`Modulation: ${formatNumber(load, 0)} ${loadUnit}`}>
            <span class="hv-cascade__load-fill"
                  style={{ width: `${clamp(load, 0, 100)}%` }} />
            <span class="hv-cascade__load-text">{Math.round(load)} {loadUnit}</span>
          </div>
        ) : (
          <span class="hv-cascade__load-empty">—</span>
        )}
      </div>

      {runtimeHours != null && (
        <div class="hv-cascade__runtime-col" title="Betriebsstunden gesamt">
          {formatRuntime(runtimeHours)}
        </div>
      )}
    </div>
  );
}

// ---------- helpers -------------------------------------------------------

function classifyState(pv: PointValue | null): StageStatus {
  if (!pv || pv.value == null) return 'unknown';
  const raw = pv.value;

  if (typeof raw === 'number') {
    if (raw === 0) return 'off';
    if (raw === 1) return 'released';
    return 'running';
  }
  if (typeof raw === 'boolean') return raw ? 'running' : 'off';
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (['off', 'aus', '0', 'stopped'].includes(s)) return 'off';
    if (['released', 'freigabe', 'ready', 'standby', 'enabled', '1'].includes(s)) return 'released';
    if (['running', 'laeuft', 'läuft', 'run', 'on', '2'].includes(s)) return 'running';
    if (s.startsWith('off') || s.startsWith('aus')) return 'off';
    if (s.startsWith('run') || s.startsWith('lauf') || s.startsWith('läuf')) return 'running';
    if (s.startsWith('free') || s.startsWith('freig') || s.startsWith('rel')) return 'released';
  }
  return 'unknown';
}

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  return null;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Runtime formatting: "3d 5h" when under 100 hours (so short-runtime pumps
 *  read naturally), plain "1245 h" once past that. Concept: operators care
 *  about "hat der Kessel heute schon gelaufen?" for new stages, but for a
 *  stage with 15 years of runtime the days breakdown is noise. */
function formatRuntime(hours: number): string {
  if (hours < 100) {
    const days = Math.floor(hours / 24);
    const remH = Math.floor(hours - days * 24);
    if (days > 0) return `${days}d ${remH}h`;
    return `${remH}h`;
  }
  return `${formatNumber(Math.round(hours), 0)} h`;
}
